import React, {useState, useCallback, useRef, useEffect, useMemo} from 'react';
import ReactDOM from 'react-dom';
import {
    Search, Plus, X, Loader, CheckCircle, AlertCircle,
    Edit3, Trash2, Copy, ToggleLeft, ToggleRight,
    ChevronDown, ChevronUp, ChevronsDownUp, ChevronsUpDown, Clock, BarChart2,
    BookOpen, Tag, Layers, ChevronRight,
    Eye, ArrowUpDown
} from 'lucide-react';
import ActionMenu from '../../Common/ActionMenu';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {MdClose} from 'react-icons/md';
import secureStorage from '../../../utils/secureStorage';
import {api} from '../../../config/api';
import toast from 'react-hot-toast';
import ProductDetailsModal from '../product/ProductDetailsModal';
import ColorPalette from '../ColorPalette';
import AppleSelect from '../../Common/AppleSelect';

// ─── helpers ────────────────────────────────────────────────────────────────

const getHeaders = async () => {
    const acc = await secureStorage.getItem('selectedAccount');
    return {'X-account-id': acc.id, 'Content-Type': 'application/json'};
};

const EMPTY_GROUP = () => ({
    _key: Date.now() + Math.random(),
    name: '',
    required: true,
    min: 1,
    max: 1,
    sort_order: 0,
    items: [],
});

const EMPTY_ITEM = () => ({
    _key: Date.now() + Math.random(),
    variant_id: '',
    product_id: '',
    label: '',
    price_delta: 0,
    sort_order: 0,
});

const EMPTY_FORM = {
    name: '',
    price_strategy: 'fixed',
    price: '',
    active: true,
    sort_order: 0,
    description: '',
    available_from: '',
    available_to: '',
    groups: [EMPTY_GROUP()],
};

// Menu/pack thumbnail: uploaded image if present, otherwise colored initials.
const PackThumb = ({name, color, imageUrl, className = 'w-10 h-10'}) => {
    if (imageUrl) {
        return <img src={imageUrl} alt="" className={`${className} object-cover flex-shrink-0`}/>;
    }
    return (
        <div
            className={`${className} flex-shrink-0 flex items-center justify-center text-white text-sm font-bold shadow-sm`}
            style={{backgroundColor: color || '#4DB6AC'}}
        >
            {(name || '?').substring(0, 2).toUpperCase()}
        </div>
    );
};

// ─── VariantPicker ───────────────────────────────────────────────────────────
// Dropdown is rendered via a React portal so it escapes overflow:hidden / scroll
// containers inside the modal. Position is calculated from the input's bounding rect.

// Small thumbnail with a colored-initials fallback when a product has no image.
const ProductThumb = ({name, imageUrl, size = 8}) => {
    const px = size === 7 ? 'w-7 h-7' : size === 9 ? 'w-9 h-9' : 'w-8 h-8';
    if (imageUrl) {
        return <img src={imageUrl} alt="" className={`${px} rounded-lg object-cover flex-shrink-0`}/>;
    }
    return (
        <div className={`${px} rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0`}>
            {(name || '?').substring(0, 2).toUpperCase()}
        </div>
    );
};

const VariantPicker = ({variantId, productId, label, image, previewProductId, onSelect, onClear}) => {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [pickedProduct, setPickedProduct] = useState(null);
    const [searching, setSearching] = useState(false);
    const [results, setResults] = useState([]);
    const [dropPos, setDropPos] = useState({top: 0, left: 0, width: 0});
    const [highlight, setHighlight] = useState(-1);
    const [previewId, setPreviewId] = useState(null);
    const inputRef = useRef(null);
    const portalRef = useRef(null);
    const debounceRef = useRef(null);
    const defaultLoadedRef = useRef(false);

    // Recompute dropdown position whenever it opens or the window scrolls/resizes
    const updatePos = useCallback(() => {
        if (!inputRef.current) return;
        const r = inputRef.current.getBoundingClientRect();
        setDropPos({top: r.bottom + window.scrollY + 4, left: r.left + window.scrollX, width: r.width});
    }, []);

    useEffect(() => {
        if (!open) return;
        updatePos();
        window.addEventListener('scroll', updatePos, true);
        window.addEventListener('resize', updatePos);
        return () => {
            window.removeEventListener('scroll', updatePos, true);
            window.removeEventListener('resize', updatePos);
        };
    }, [open, updatePos]);

    // Close on outside click (check both the input wrapper and the portal)
    useEffect(() => {
        const handler = (e) => {
            if (
                inputRef.current && !inputRef.current.closest('[data-variant-picker]')?.contains(e.target) &&
                portalRef.current && !portalRef.current.contains(e.target)
            ) {
                setOpen(false);
                setPickedProduct(null);
                setQuery('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const runSearch = async (q) => {
        setSearching(true);
        try {
            const acc = await secureStorage.getItem('selectedAccount');
            const url = q.trim()
                ? `/products?name=${encodeURIComponent(q.trim())}&itemsPerPage=20`
                : `/products?itemsPerPage=20`;
            const res = await api.get(url, {headers: {'X-account-id': acc.id}});
            const data = res.data?.data ?? res.data ?? [];
            setResults(Array.isArray(data) ? data : []);
        } catch {
            setResults([]);
        } finally {
            setSearching(false);
        }
    };

    const searchProducts = (q) => {
        clearTimeout(debounceRef.current);
        if (!q.trim()) { runSearch(''); return; }
        debounceRef.current = setTimeout(() => runSearch(q), 200);
    };

    // Browse the first products immediately on focus, before the user types anything.
    const loadDefaultResults = () => {
        if (defaultLoadedRef.current) return;
        defaultLoadedRef.current = true;
        runSearch('');
    };

    const handleQueryChange = (e) => {
        const q = e.target.value;
        setQuery(q);
        setPickedProduct(null);
        setOpen(true);
        setHighlight(-1);
        searchProducts(q);
    };

    const pickProduct = (product) => {
        setPickedProduct(product);
        setQuery('');
        setResults([]);
        setHighlight(-1);
        updatePos();
    };

    const pickVariant = (product, variant) => {
        const variantLabel = variant.name ? `${product.name} — ${variant.name}` : product.name;
        onSelect({variant_id: variant.id, product_id: '', label: variantLabel, image: product.imageUrl, _previewProductId: product.id});
        setOpen(false);
        setPickedProduct(null);
        setQuery('');
    };

    const selectProductOnly = (product) => {
        onSelect({variant_id: '', product_id: product.id, label: product.name, image: product.imageUrl, _previewProductId: product.id});
        setOpen(false);
        setPickedProduct(null);
        setQuery('');
    };

    // Keyboard navigation: ArrowUp/Down to move, Enter to pick, Escape to close.
    const currentList = pickedProduct
        ? ((!pickedProduct.variants || pickedProduct.variants.length === 0) ? ['__product_only__'] : pickedProduct.variants)
        : results;

    const handleKeyDown = (e) => {
        if (!open) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight(h => Math.min(h + 1, currentList.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight(h => Math.max(h - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (highlight < 0 || highlight >= currentList.length) return;
            if (pickedProduct) {
                const entry = currentList[highlight];
                if (entry === '__product_only__') selectProductOnly(pickedProduct);
                else pickVariant(pickedProduct, entry);
            } else {
                pickProduct(currentList[highlight]);
            }
        } else if (e.key === 'Escape') {
            setOpen(false);
            setPickedProduct(null);
            setQuery('');
            inputRef.current?.blur();
        }
    };

    const dropdownContent = (open && (query.trim() || pickedProduct || results.length > 0)) ? (
        <div
            ref={portalRef}
            style={{position: 'absolute', top: dropPos.top, left: dropPos.left, width: dropPos.width, zIndex: 9999}}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl max-h-80 overflow-y-auto"
        >
            {!pickedProduct && (
                <>
                    {!query.trim() && results.length > 0 && !searching && (
                        <p className="px-3 pt-2.5 pb-1 text-[11px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">Suggestions</p>
                    )}
                    {results.length === 0 && !searching && query.trim() && (
                        <p className="px-3 py-3 text-xs text-gray-400 dark:text-gray-500 text-center">Aucun produit trouvé</p>
                    )}
                    {searching && results.length === 0 && (
                        <p className="px-3 py-3 text-xs text-gray-400 dark:text-gray-500 text-center flex items-center justify-center gap-1.5">
                            <Loader className="w-3 h-3 animate-spin"/> Recherche…
                        </p>
                    )}
                    {results.map((product, i) => (
                        <div
                            key={product.id}
                            onMouseEnter={() => setHighlight(i)}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-left transition ${highlight === i ? 'bg-teal-50 dark:bg-teal-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                        >
                            <button
                                type="button"
                                onMouseDown={e => e.preventDefault()}
                                onClick={() => pickProduct(product)}
                                className="flex items-center gap-2 min-w-0 flex-1"
                            >
                                <ProductThumb name={product.name} imageUrl={product.imageUrl}/>
                                <div className="min-w-0">
                                    <p className="text-xs font-medium text-gray-800 dark:text-gray-100 truncate">{product.name}</p>
                                    {product.variants?.length > 0 && (
                                        <p className="text-xs text-gray-400 dark:text-gray-500">{product.variants.length} variante{product.variants.length > 1 ? 's' : ''}</p>
                                    )}
                                </div>
                            </button>
                            <button
                                type="button"
                                onMouseDown={e => e.preventDefault()}
                                onClick={() => setPreviewId(product.id)}
                                className="text-gray-300 dark:text-gray-600 hover:text-teal-600 flex-shrink-0 p-1"
                                title="Voir le détail"
                            >
                                <Eye className="w-3.5 h-3.5"/>
                            </button>
                            <ChevronRight className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 flex-shrink-0"/>
                        </div>
                    ))}
                </>
            )}

            {pickedProduct && (
                <>
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 sticky top-0">
                        <button
                            type="button"
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => setPickedProduct(null)}
                            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
                        >
                            <ChevronDown className="w-3.5 h-3.5"/>
                        </button>
                        <ProductThumb name={pickedProduct.name} imageUrl={pickedProduct.imageUrl} size={7}/>
                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 truncate flex-1">{pickedProduct.name}</span>
                        <button
                            type="button"
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => setPreviewId(pickedProduct.id)}
                            className="text-gray-300 hover:text-teal-600 flex-shrink-0 p-1"
                            title="Voir le détail"
                        >
                            <Eye className="w-3.5 h-3.5"/>
                        </button>
                    </div>
                    {(!pickedProduct.variants || pickedProduct.variants.length === 0) ? (
                        <button
                            type="button"
                            onMouseDown={e => e.preventDefault()}
                            onMouseEnter={() => setHighlight(0)}
                            onClick={() => selectProductOnly(pickedProduct)}
                            className={`w-full px-3 py-2.5 text-left text-xs text-gray-700 dark:text-gray-300 transition ${highlight === 0 ? 'bg-teal-50 dark:bg-teal-900/30' : 'hover:bg-teal-50 dark:hover:bg-teal-900/30'}`}
                        >
                            Sélectionner ce produit (sans variante)
                        </button>
                    ) : (
                        pickedProduct.variants.map((v, i) => (
                            <button
                                key={v.id}
                                type="button"
                                onMouseDown={e => e.preventDefault()}
                                onMouseEnter={() => setHighlight(i)}
                                onClick={() => pickVariant(pickedProduct, v)}
                                className={`w-full flex items-center justify-between px-3 py-2.5 text-left transition ${highlight === i ? 'bg-teal-50 dark:bg-teal-900/30' : 'hover:bg-teal-50 dark:hover:bg-teal-900/30'}`}
                            >
                                <div className="min-w-0">
                                    <p className="text-xs font-medium text-gray-800 dark:text-gray-100 truncate">{v.name || 'Variante par défaut'}</p>
                                    {v.price != null && <p className="text-xs text-gray-400 dark:text-gray-500">{Number(v.price).toFixed(2)} MAD</p>}
                                </div>
                                <CheckCircle className="w-3.5 h-3.5 text-teal-400 flex-shrink-0 ml-2"/>
                            </button>
                        ))
                    )}
                </>
            )}
        </div>
    ) : null;

    // If a product or variant is already selected, show its label chip
    if (variantId || productId) {
        const detailsId = previewProductId || productId || null;
        return (
            <div className="flex items-center gap-2 flex-1 min-w-0 bg-teal-50 dark:bg-teal-900/30 border border-teal-200 dark:border-teal-800 rounded-lg pl-2 pr-3 py-1.5">
                <ProductThumb name={label} imageUrl={image}/>
                <span className="text-xs text-teal-800 dark:text-teal-300 font-medium truncate flex-1">{label || variantId || productId}</span>
                {detailsId && (
                    <button
                        type="button"
                        onClick={() => setPreviewId(detailsId)}
                        className="text-teal-400 dark:text-teal-500 hover:text-teal-700 dark:hover:text-teal-300 flex-shrink-0"
                        title="Voir le détail"
                    >
                        <Eye className="w-3.5 h-3.5"/>
                    </button>
                )}
                <button type="button" onClick={onClear} className="text-teal-400 dark:text-teal-500 hover:text-red-500 flex-shrink-0">
                    <X className="w-3.5 h-3.5"/>
                </button>
                {previewId && (
                    <ProductDetailsModal productId={previewId} isOpen={!!previewId} onClose={() => setPreviewId(null)}/>
                )}
            </div>
        );
    }

    return (
        <div data-variant-picker className="relative flex-1">
            <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"/>
                <input
                    ref={inputRef}
                    value={query}
                    onChange={handleQueryChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => { setOpen(true); updatePos(); if (query) searchProducts(query); else loadDefaultResults(); }}
                    onBlur={() => {
                        // Delay close so portal clicks register first
                        setTimeout(() => {
                            if (!portalRef.current?.contains(document.activeElement)) {
                                setOpen(false);
                                setPickedProduct(null);
                                setQuery('');
                            }
                        }, 150);
                    }}
                    placeholder="Rechercher un produit…"
                    className="w-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg pl-7 pr-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-teal-400"
                />
                {searching && <Loader className="w-3 h-3 absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 animate-spin"/>}
            </div>
            {ReactDOM.createPortal(dropdownContent, document.body)}
            {previewId && (
                <ProductDetailsModal productId={previewId} isOpen={!!previewId} onClose={() => setPreviewId(null)}/>
            )}
        </div>
    );
};

// ─── sub-components ──────────────────────────────────────────────────────────

const Badge = ({active}) => active
    ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300"><CheckCircle className="w-3 h-3"/>Actif</span>
    : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">Inactif</span>;

const StrategyBadge = ({strategy}) => strategy === 'fixed'
    ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300"><Tag className="w-3 h-3"/>Prix fixe</span>
    : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300"><Layers className="w-3 h-3"/>Prix dynamique</span>;

// ─── SortableHeader ───────────────────────────────────────────────────────────

const SortableHeader = ({field, currentField, onSort, children}) => {
    const isActive = currentField === field;
    return (
        <th
            className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
            onClick={() => onSort(field)}
        >
            <div className="flex items-center gap-1">
                {children}
                <ArrowUpDown className={`w-3 h-3 ${isActive ? 'text-primary' : 'text-gray-400'}`} />
            </div>
        </th>
    );
};

// ─── MenuActions ──────────────────────────────────────────────────────────────

// Row actions: one "⋯" menu for desktop rows and mobile cards
const MenuActions = ({menu, onEdit, onToggle, onDuplicate, onDelete, onViewDetails}) => (
    <ActionMenu
        label={`Actions pour ${menu.name}`}
        items={[
            {label: 'Modifier', icon: Edit3, onClick: () => onEdit(menu)},
            {label: 'Détails', icon: Eye, onClick: () => onViewDetails(menu)},
            {label: 'Dupliquer', icon: Copy, onClick: () => onDuplicate(menu)},
            {divider: true},
            {label: menu.active ? 'Désactiver' : 'Activer', icon: menu.active ? ToggleLeft : ToggleRight, onClick: () => onToggle(menu)},
            {label: 'Supprimer', icon: Trash2, danger: true, onClick: () => onDelete(menu)},
        ]}
    />
);

// ─── MenuDetailsModal ─────────────────────────────────────────────────────────

const MenuDetailsModal = ({menu, onClose, onEdit}) => {
    const groupCount = menu.groups?.length ?? 0;
    const itemCount = menu.groups?.reduce((s, g) => s + (g.items?.length ?? 0), 0) ?? 0;
    const [resolvedLabels, setResolvedLabels] = useState({});

    useEffect(() => {
        let cancelled = false;

        const resolveLabels = async () => {
            const acc = await secureStorage.getItem('selectedAccount');
            const headers = {'X-account-id': acc.id};
            const productCache = {};

            const fetchProduct = async (id) => {
                if (productCache[id]) return productCache[id];
                try {
                    const res = await api.get(`/products/${id}`, {headers});
                    const p = res.data?.data ?? res.data;
                    productCache[id] = p;
                    return p;
                } catch { return null; }
            };

            const updates = {};

            for (const group of (menu.groups ?? [])) {
                for (const item of (group.items ?? [])) {
                    const existing = item.label || item.variant_name || item.product_name || item.name;
                    if (existing || (!item.variant_id && !item.product_id)) continue;
                    const key = item.id ?? `${item.variant_id}${item.product_id}`;
                    let label = '';
                    if (item.product_id) {
                        const p = await fetchProduct(item.product_id);
                        label = p?.name ?? '';
                    } else if (item.variant_id) {
                        try {
                            const res = await api.get(`/products?itemsPerPage=200`, {headers});
                            const products = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
                            for (const p of products) {
                                const v = p.variants?.find(v => v.id === item.variant_id);
                                if (v) { label = v.name ? `${p.name} — ${v.name}` : p.name; break; }
                            }
                        } catch { /* keep empty */ }
                    }
                    if (label) updates[key] = label;
                }
            }

            if (!cancelled && Object.keys(updates).length > 0) setResolvedLabels(updates);
        };

        resolveLabels();
        return () => { cancelled = true; };
    }, [menu]);

    const getItemLabel = (item) => {
        const key = item.id ?? `${item.variant_id}${item.product_id}`;
        return item.label || item.variant_name || item.product_name || item.name || resolvedLabels[key] || '…';
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <PackThumb name={menu.name} color={menu.color} imageUrl={menu.image_url} className="w-10 h-10 rounded-lg"/>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">{menu.name}</h2>
                            {menu.description && <p className="text-xs text-gray-500 dark:text-gray-400">{menu.description}</p>}
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition">
                        <X className="w-5 h-5 text-gray-500 dark:text-gray-400"/>
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Info badges */}
                    <div className="flex flex-wrap gap-2">
                        <Badge active={menu.active}/>
                        <StrategyBadge strategy={menu.price_strategy}/>
                        {menu.price_strategy === 'fixed' && menu.price != null && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                {Number(menu.price).toFixed(2)} MAD
                            </span>
                        )}
                    </div>

                    {/* Summary cards */}
                    <div className="grid grid-cols-3 gap-3">
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{groupCount}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Groupes</p>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{itemCount}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Articles</p>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{menu.sort_order ?? 0}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Ordre</p>
                        </div>
                    </div>

                    {/* Availability */}
                    {(menu.available_from || menu.available_to) && (
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 bg-teal-50 dark:bg-teal-900/30 px-4 py-3 rounded-xl">
                            <Clock className="w-4 h-4 text-teal-600 dark:text-teal-400"/>
                            <span>Disponible : </span>
                            <span className="font-medium">
                                {menu.available_from && menu.available_to
                                    ? `${menu.available_from} – ${menu.available_to}`
                                    : menu.available_from ? `Dès ${menu.available_from}` : `Jusqu'à ${menu.available_to}`
                                }
                            </span>
                        </div>
                    )}

                    {/* Groups */}
                    <div>
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3">
                            Groupes & Articles
                        </h3>
                        {(!menu.groups || menu.groups.length === 0) ? (
                            <p className="text-sm text-gray-400 dark:text-gray-500 italic">Aucun groupe défini.</p>
                        ) : (
                            <div className="space-y-3">
                                {menu.groups.map((group, gIdx) => (
                                    <div key={group.id ?? gIdx} className="border border-gray-200 dark:border-gray-600 rounded-xl overflow-hidden">
                                        <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-3 flex items-center justify-between">
                                            <div>
                                                <span className="font-medium text-gray-800 dark:text-gray-200">{group.name}</span>
                                                {group.required && <span className="ml-2 text-xs text-red-500 dark:text-red-400">*obligatoire</span>}
                                            </div>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                Min: {group.min} | Max: {group.max}
                                            </span>
                                        </div>
                                        {group.items && group.items.length > 0 && (
                                            <div className="divide-y divide-gray-100 dark:divide-gray-700">
                                                {group.items.map((item, iIdx) => (
                                                    <div key={item.id ?? iIdx} className="px-4 py-2 flex items-center justify-between text-sm">
                                                        <span className="text-gray-700 dark:text-gray-300">
                                                            {getItemLabel(item)}
                                                        </span>
                                                        {item.price_delta !== 0 && (
                                                            <span className={`text-xs font-medium ${item.price_delta > 0 ? 'text-green-600' : 'text-red-500'}`}>
                                                                {item.price_delta > 0 ? '+' : ''}{Number(item.price_delta).toFixed(2)} MAD
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex gap-3 p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
                    <button
                        onClick={onClose}
                        className="flex-1 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                    >
                        Fermer
                    </button>
                    <button
                        onClick={() => { onClose(); onEdit(menu); }}
                        className="flex-1 py-2.5 bg-[#4DB6AC] hover:bg-teal-600 text-white rounded-xl text-sm font-medium transition flex items-center justify-center gap-2"
                    >
                        <Edit3 className="w-4 h-4"/> Modifier
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── BulkDeleteConfirmModal ───────────────────────────────────────────────────

const BulkDeleteConfirmModal = ({count, onConfirm, onCancel, loading, menuWord = 'menu'}) => (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-500"/>
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 text-center mb-2">Supprimer les {menuWord}s</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
                Voulez-vous vraiment supprimer <span className="font-semibold text-gray-700 dark:text-gray-300">{count} {menuWord}{count > 1 ? 's' : ''}</span> ? Cette action est irréversible.
            </p>
            <div className="flex gap-3">
                <button onClick={onCancel} className="flex-1 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                    Annuler
                </button>
                <button onClick={onConfirm} disabled={loading}
                    className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-medium transition flex items-center justify-center gap-2 disabled:opacity-60">
                    {loading && <Loader className="w-4 h-4 animate-spin"/>}
                    Supprimer
                </button>
            </div>
        </div>
    </div>
);

// ─── GroupEditor ─────────────────────────────────────────────────────────────

const GroupEditor = ({group, groupIndex, onChange, onRemove, collapsed, onToggleCollapse}) => {
    const open = !collapsed;

    const updateField = (field, value) => onChange(groupIndex, {...group, [field]: value});

    const addItem = () => onChange(groupIndex, {...group, items: [...group.items, EMPTY_ITEM()]});

    const updateItem = (idx, patch) => {
        const items = group.items.map((it, i) => i === idx ? {...it, ...patch} : it);
        onChange(groupIndex, {...group, items});
    };

    const removeItem = (idx) => onChange(groupIndex, {...group, items: group.items.filter((_, i) => i !== idx)});

    return (
        <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900/40 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition text-left"
                onClick={onToggleCollapse}
            >
                <span className="flex items-center gap-2 min-w-0 font-medium text-sm text-gray-800 dark:text-gray-100">
                    <span className="truncate">{group.name || `Groupe ${groupIndex + 1}`}</span>
                    {group.required && <span className="text-xs text-red-500 flex-shrink-0">*obligatoire</span>}
                    {group.items.length > 0 && (
                        <span className="flex-shrink-0 px-1.5 py-0.5 rounded-full text-[11px] font-semibold bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300">
                            {group.items.length} article{group.items.length > 1 ? 's' : ''}
                        </span>
                    )}
                </span>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                        type="button"
                        className="text-red-400 hover:text-red-600 p-1 rounded"
                        onClick={(e) => {e.stopPropagation(); onRemove(groupIndex);}}
                    >
                        <Trash2 className="w-4 h-4"/>
                    </button>
                    <ChevronDown className={`w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}/>
                </div>
            </button>

            <div className={`grid transition-[grid-template-rows] duration-200 ease-in-out ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                <div className="overflow-hidden">
                    <div className="p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Nom du groupe</label>
                            <input
                                className="w-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                                value={group.name}
                                onChange={e => updateField('name', e.target.value)}
                                placeholder="Ex: Plat principal"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Minimum</label>
                            <input
                                type="number" min={0}
                                className="w-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                                value={group.min}
                                onChange={e => updateField('min', parseInt(e.target.value) || 0)}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Maximum</label>
                            <input
                                type="number" min={1}
                                className="w-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                                value={group.max}
                                onChange={e => updateField('max', parseInt(e.target.value) || 1)}
                            />
                        </div>
                        <div className="col-span-2 flex items-center gap-2">
                            <input
                                type="checkbox"
                                id={`req-${groupIndex}`}
                                checked={group.required}
                                onChange={e => updateField('required', e.target.checked)}
                                className="w-4 h-4 accent-teal-500"
                            />
                            <label htmlFor={`req-${groupIndex}`} className="text-sm text-gray-700 dark:text-gray-300">Sélection obligatoire</label>
                        </div>
                    </div>

                    {/* Items */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Articles</span>
                            <button
                                type="button"
                                className="text-xs text-teal-600 hover:text-teal-800 flex items-center gap-1"
                                onClick={addItem}
                            >
                                <Plus className="w-3 h-3"/> Ajouter
                            </button>
                        </div>
                        {group.items.length === 0 && (
                            <p className="text-xs text-gray-400 italic">Aucun article. Cliquez sur "Ajouter".</p>
                        )}
                        <div className="space-y-2">
                            {group.items.map((item, idx) => (
                                <div key={item._key ?? idx} className="flex gap-2 items-center">
                                    <VariantPicker
                                        variantId={item.variant_id}
                                        productId={item.product_id}
                                        label={item.label}
                                        image={item.image}
                                        previewProductId={item._previewProductId}
                                        onSelect={(sel) => updateItem(idx, sel)}
                                        onClear={() => updateItem(idx, {variant_id: '', product_id: '', label: '', image: '', _previewProductId: ''})}
                                    />
                                    <div className="relative flex-shrink-0">
                                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">Δ</span>
                                        <input
                                            type="number"
                                            className="w-20 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg pl-5 pr-2 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-teal-400"
                                            placeholder="0"
                                            value={item.price_delta}
                                            onChange={e => updateItem(idx, {price_delta: parseFloat(e.target.value) || 0})}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className="text-red-400 hover:text-red-600 p-1 flex-shrink-0"
                                        onClick={() => removeItem(idx)}
                                    >
                                        <X className="w-3.5 h-3.5"/>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── Stepper indicator ───────────────────────────────────────────────────────

const STEPS = [
    {label: 'Informations'},
    {label: 'Prix'},
    {label: 'Groupes'},
    {label: 'Récapitulatif'},
];

const StepIndicator = ({current}) => (
    <div className="flex items-center px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex-shrink-0 bg-gray-50 dark:bg-gray-900/40">
        {STEPS.map((s, i) => {
            const done = i < current;
            const active = i === current;
            return (
                <React.Fragment key={i}>
                    <div className="flex flex-col items-center min-w-0">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                            done ? 'bg-[#4DB6AC] text-white' :
                            active ? 'bg-[#4DB6AC] text-white ring-4 ring-teal-100 dark:ring-teal-900/40' :
                            'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                        }`}>
                            {done ? <CheckCircle className="w-4 h-4"/> : i + 1}
                        </div>
                        <span className={`text-xs mt-1 whitespace-nowrap font-medium ${active ? 'text-[#4DB6AC]' : done ? 'text-teal-600 dark:text-teal-400' : 'text-gray-400 dark:text-gray-500'}`}>
                            {s.label}
                        </span>
                    </div>
                    {i < STEPS.length - 1 && (
                        <div className={`flex-1 h-0.5 mx-2 mb-4 transition-all ${i < current ? 'bg-[#4DB6AC]' : 'bg-gray-200 dark:bg-gray-700'}`}/>
                    )}
                </React.Fragment>
            );
        })}
    </div>
);

// ─── MenuFormModal ────────────────────────────────────────────────────────────

const MenuFormModal = ({initial, onClose, onSaved, menuWord = 'menu'}) => {
    const isEdit = !!initial?.id;
    const MenuWord = menuWord.charAt(0).toUpperCase() + menuWord.slice(1);
    const [step, setStep] = useState(0);
    const [currentColor, setCurrentColor] = useState(() => initial?.color || '#4DB6AC');
    const [packImage, setPackImage] = useState(() => initial?.image_url || null);
    const [showPalette, setShowPalette] = useState(false);
    const [form, setForm] = useState(() => {
        if (!initial) return EMPTY_FORM;
        return {
            name: initial.name ?? '',
            price_strategy: initial.price_strategy ?? 'fixed',
            price: initial.price ?? '',
            active: initial.active ?? true,
            sort_order: initial.sort_order ?? 0,
            description: initial.description ?? '',
            available_from: initial.available_from ?? '',
            available_to: initial.available_to ?? '',
            groups: (initial.groups ?? []).map(g => ({
                ...g,
                _key: g.id ?? Math.random(),
                items: (g.items ?? []).map(it => ({
                    ...it,
                    _key: it.id ?? Math.random(),
                    label: it.label ?? it.variant_name ?? it.product_name ?? it.name ?? '',
                })),
            })),
        };
    });
    const [saving, setSaving] = useState(false);

    // Resolve product/variant display metadata (name + thumbnail) for items loaded
    // from the API — the menu payload only stores variant_id/product_id, not the
    // image, so the picker's thumbnail/preview would otherwise stay empty for
    // pre-existing items until they're re-picked.
    useEffect(() => {
        if (!isEdit) return;
        let cancelled = false;

        const resolveItemsMeta = async () => {
            const acc = await secureStorage.getItem('selectedAccount');
            const headers = {'X-account-id': acc.id};
            const productCache = {};
            let allProducts = null;

            const fetchProduct = async (id) => {
                if (productCache[id]) return productCache[id];
                try {
                    const res = await api.get(`/products/${id}`, {headers});
                    const p = res.data?.data ?? res.data;
                    productCache[id] = p;
                    return p;
                } catch {
                    return null;
                }
            };

            const findProductByVariant = async (variantId) => {
                if (!allProducts) {
                    try {
                        const res = await api.get(`/products?itemsPerPage=200`, {headers});
                        allProducts = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
                    } catch {
                        allProducts = [];
                    }
                }
                for (const p of allProducts) {
                    const v = p.variants?.find(v => v.id === variantId);
                    if (v) return {product: p, variant: v};
                }
                return null;
            };

            const groupUpdates = {};
            for (const [gi, group] of form.groups.entries()) {
                for (const [ii, item] of group.items.entries()) {
                    if (!item.variant_id && !item.product_id) continue;
                    if (item.label && item.image) continue; // already fully resolved

                    let label = item.label || '';
                    let image = item.image || '';
                    let previewProductId = item._previewProductId || '';

                    if (item.product_id) {
                        const p = await fetchProduct(item.product_id);
                        if (p) {
                            label = label || (p.name ?? '');
                            image = image || (p.imageUrl ?? '');
                            previewProductId = previewProductId || p.id;
                        }
                    } else if (item.variant_id) {
                        const found = await findProductByVariant(item.variant_id);
                        if (found) {
                            const {product: p, variant: v} = found;
                            label = label || (v.name ? `${p.name} — ${v.name}` : p.name);
                            image = image || (p.imageUrl ?? '');
                            previewProductId = previewProductId || p.id;
                        }
                    }

                    if (label !== item.label || image !== item.image || previewProductId !== item._previewProductId) {
                        if (!groupUpdates[gi]) groupUpdates[gi] = {};
                        groupUpdates[gi][ii] = {label, image, _previewProductId: previewProductId};
                    }
                }
            }

            if (!cancelled && Object.keys(groupUpdates).length > 0) {
                setForm(prev => ({
                    ...prev,
                    groups: prev.groups.map((g, gi) => {
                        if (!groupUpdates[gi]) return g;
                        return {
                            ...g,
                            items: g.items.map((it, ii) =>
                                groupUpdates[gi][ii] ? {...it, ...groupUpdates[gi][ii]} : it
                            ),
                        };
                    }),
                }));
            }
        };

        resolveItemsMeta();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setField = (field, value) => setForm(f => ({...f, [field]: value}));
    const addGroup = () => setForm(f => ({...f, groups: [...f.groups, EMPTY_GROUP()]}));
    const updateGroup = useCallback((idx, group) => {
        setForm(f => ({...f, groups: f.groups.map((g, i) => i === idx ? group : g)}));
    }, []);
    const removeGroup = (idx) => setForm(f => ({...f, groups: f.groups.filter((_, i) => i !== idx)}));

    // Per-group collapse state, keyed by the group's stable `_key` (all expanded by default).
    const [collapsedKeys, setCollapsedKeys] = useState(() => new Set());
    const toggleGroupCollapsed = useCallback((key) => {
        setCollapsedKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    }, []);
    const allGroupsCollapsed = form.groups.length > 0 && form.groups.every(g => collapsedKeys.has(g._key));
    const toggleCollapseAll = () => {
        setCollapsedKeys(allGroupsCollapsed ? new Set() : new Set(form.groups.map(g => g._key)));
    };

    const validateStep = () => {
        if (step === 0 && !form.name.trim()) { toast.error('Le nom est obligatoire'); return false; }
        if (step === 1 && form.price_strategy === 'fixed' && (form.price === '' || isNaN(parseFloat(form.price)))) {
            toast.error('Saisissez un prix valide'); return false;
        }
        return true;
    };

    const next = () => { if (validateStep()) setStep(s => Math.min(s + 1, STEPS.length - 1)); };
    const back = () => setStep(s => Math.max(s - 1, 0));

    const buildGroupsPayload = () => form.groups.map((g, gi) => ({
        ...(g.id ? {id: g.id} : {}),
        name: g.name,
        required: g.required,
        min: g.min,
        max: g.max,
        sort_order: gi,
        items: g.items.map((it, ii) => ({
            ...(it.id ? {id: it.id} : {}),
            ...(it.variant_id ? {variant_id: it.variant_id} : {}),
            ...(it.product_id ? {product_id: it.product_id} : {}),
            price_delta: it.price_delta,
            sort_order: ii,
        })),
    }));

    // Sent as multipart/form-data (not JSON) so the pack's image can be uploaded
    // alongside its other fields — `groups` is passed as a JSON-encoded string field.
    const buildFormData = async () => {
        const fd = new FormData();
        fd.append('name', form.name);
        fd.append('price_strategy', form.price_strategy);
        fd.append('price', form.price_strategy === 'fixed' ? String(parseFloat(form.price) || 0) : '');
        fd.append('active', String(form.active));
        fd.append('sort_order', String(parseInt(form.sort_order) || 0));
        fd.append('description', form.description || '');
        if (form.available_from) fd.append('available_from', form.available_from);
        if (form.available_to) fd.append('available_to', form.available_to);
        fd.append('color', currentColor);
        fd.append('groups', JSON.stringify(buildGroupsPayload()));

        if (packImage instanceof File) {
            fd.append('image', packImage);
        } else if (typeof packImage === 'string' && packImage.startsWith('data:')) {
            const res = await fetch(packImage);
            const blob = await res.blob();
            fd.append('image', new File([blob], 'pack-image.jpg', {type: blob.type || 'image/jpeg'}));
        }
        return fd;
    };

    const handleSubmit = async () => {
        setSaving(true);
        try {
            const acc = await secureStorage.getItem('selectedAccount');
            const headers = {'X-account-id': acc.id, 'Content-Type': 'multipart/form-data'};
            const fd = await buildFormData();
            if (isEdit) {
                await api.post(`/menus/${initial.id}/update`, fd, {headers});
                toast.success(`${MenuWord} mis à jour`);
            } else {
                await api.post('/menus', fd, {headers});
                toast.success(`${MenuWord} créé`);
            }
            onSaved();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    const handleColorChange = (color) => {
        setCurrentColor(color);
        setShowPalette(false);
    };
    const handleImageChange = (image) => setPackImage(image);
    const togglePalette = () => setShowPalette(p => !p);
    const handleDeleteImage = () => setPackImage(null);

    // ── step panels ──────────────────────────────────────────────────────────

    const Step1 = (
        <div className="space-y-5">
            <div className="flex flex-col items-center space-y-2">
                {packImage ? (
                    <div className="relative w-20 h-20">
                        <img
                            src={packImage}
                            alt=""
                            className="w-20 h-20 object-cover rounded-xl shadow-sm cursor-pointer"
                            onClick={togglePalette}
                        />
                        <button
                            type="button"
                            onClick={handleDeleteImage}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 shadow-sm hover:bg-red-600 transition-colors"
                            aria-label="Supprimer l'image"
                        >
                            <MdClose className="w-3.5 h-3.5"/>
                        </button>
                    </div>
                ) : (
                    <div
                        className="w-20 h-20 rounded-xl flex items-center justify-center text-white text-xs font-medium cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                        onClick={togglePalette}
                        style={{backgroundColor: currentColor}}
                    >
                        Éditer
                    </div>
                )}
                <span className="text-xs text-gray-400">Couleur / image du {menuWord}</span>
            </div>
            <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                    Nom du {menuWord} <span className="text-red-500">*</span>
                </label>
                <input
                    autoFocus
                    className="w-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                    value={form.name}
                    onChange={e => setField('name', e.target.value)}
                    placeholder={menuWord === 'menu' ? 'Ex: Menu Midi' : 'Ex: Pack Découverte'}
                />
            </div>
            <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Description</label>
                <textarea
                    rows={3}
                    className="w-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-xl px-4 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-400"
                    value={form.description}
                    onChange={e => setField('description', e.target.value)}
                    placeholder="Description optionnelle…"
                />
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Ordre d'affichage</label>
                    <input
                        type="number" min={0}
                        className="w-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                        value={form.sort_order}
                        onChange={e => setField('sort_order', e.target.value)}
                    />
                </div>
                <div className="flex flex-col justify-end pb-0.5">
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Statut</label>
                    <button
                        type="button"
                        onClick={() => setField('active', !form.active)}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition ${
                            form.active ? 'border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300' : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                        }`}
                    >
                        {form.active
                            ? <ToggleRight className="w-5 h-5 text-teal-500"/>
                            : <ToggleLeft className="w-5 h-5 text-gray-400"/>
                        }
                        {form.active ? 'Actif' : 'Inactif'}
                    </button>
                </div>
            </div>
        </div>
    );

    const Step2 = (
        <div className="space-y-5">
            <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">Stratégie de prix</label>
                <div className="grid grid-cols-2 gap-3">
                    {[
                        {value: 'fixed', label: 'Prix fixe', sub: `Un montant unique pour le ${menuWord}`, icon: Tag},
                        {value: 'dynamic', label: 'Prix dynamique', sub: 'Calculé depuis les articles choisis', icon: Layers},
                    ].map(({value, label, sub, icon: Icon}) => (
                        <button
                            key={value}
                            type="button"
                            onClick={() => setField('price_strategy', value)}
                            className={`flex flex-col items-start gap-1 p-4 rounded-xl border-2 text-left transition ${
                                form.price_strategy === value
                                    ? 'border-[#4DB6AC] bg-teal-50 dark:bg-teal-900/30'
                                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-700'
                            }`}
                        >
                            <Icon className={`w-5 h-5 mb-1 ${form.price_strategy === value ? 'text-teal-600' : 'text-gray-400'}`}/>
                            <span className={`text-sm font-semibold ${form.price_strategy === value ? 'text-teal-700 dark:text-teal-300' : 'text-gray-700 dark:text-gray-300'}`}>{label}</span>
                            <span className="text-xs text-gray-400">{sub}</span>
                        </button>
                    ))}
                </div>
            </div>

            {form.price_strategy === 'fixed' && (
                <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Prix (MAD)</label>
                    <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">MAD</span>
                        <input
                            type="number" min={0} step="0.01"
                            className="w-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-xl pl-14 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                            value={form.price}
                            onChange={e => setField('price', e.target.value)}
                            placeholder="0.00"
                        />
                    </div>
                </div>
            )}

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Disponible de</label>
                    <div className="relative">
                        <Clock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                        <input
                            type="time"
                            className="w-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-xl pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                            value={form.available_from}
                            onChange={e => setField('available_from', e.target.value)}
                        />
                    </div>
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Disponible jusqu'à</label>
                    <div className="relative">
                        <Clock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                        <input
                            type="time"
                            className="w-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-xl pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                            value={form.available_to}
                            onChange={e => setField('available_to', e.target.value)}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    const Step3 = (
        <div>
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">Groupes de choix</h3>
                    <p className="text-xs text-gray-400 mt-0.5">Chaque groupe représente une catégorie de choix pour le client.</p>
                </div>
                <div className="flex items-center gap-2">
                    {form.groups.length > 0 && (
                        <button
                            type="button"
                            onClick={toggleCollapseAll}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-lg text-xs font-semibold transition"
                        >
                            {allGroupsCollapsed
                                ? <ChevronsUpDown className="w-3.5 h-3.5"/>
                                : <ChevronsDownUp className="w-3.5 h-3.5"/>}
                            {allGroupsCollapsed ? 'Tout déplier' : 'Tout replier'}
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={addGroup}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 hover:bg-teal-100 text-teal-700 rounded-lg text-xs font-semibold transition"
                    >
                        <Plus className="w-3.5 h-3.5"/> Ajouter un groupe
                    </button>
                </div>
            </div>
            {form.groups.length === 0 ? (
                <div className="flex flex-col items-center py-10 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl text-gray-400 dark:text-gray-500">
                    <Layers className="w-10 h-10 mb-2 text-gray-200"/>
                    <p className="text-sm font-medium text-gray-400">Aucun groupe</p>
                    <p className="text-xs mt-1">Ajoutez des groupes pour définir les choix du {menuWord}.</p>
                    <button
                        type="button"
                        onClick={addGroup}
                        className="mt-3 px-4 py-1.5 bg-[#4DB6AC] text-white rounded-lg text-xs font-medium hover:bg-teal-600 transition"
                    >
                        <Plus className="w-3 h-3 inline mr-1"/>Créer un groupe
                    </button>
                </div>
            ) : (
                <div className="space-y-3">
                    {form.groups.map((g, i) => (
                        <GroupEditor
                            key={g._key ?? i}
                            group={g}
                            groupIndex={i}
                            onChange={updateGroup}
                            onRemove={removeGroup}
                            collapsed={collapsedKeys.has(g._key)}
                            onToggleCollapse={() => toggleGroupCollapsed(g._key)}
                        />
                    ))}
                </div>
            )}
        </div>
    );

    const Step4 = (
        <div className="space-y-4">
            <p className="text-xs text-gray-500 dark:text-gray-400">Vérifiez les informations avant de {isEdit ? 'enregistrer' : `créer le ${menuWord}`}.</p>

            {/* info block */}
            <div className="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-4 space-y-3">
                <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400 font-medium">Nom</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-100">{form.name || '—'}</span>
                </div>
                {form.description && (
                    <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400 font-medium">Description</span>
                        <span className="text-gray-700 dark:text-gray-300 text-right max-w-[60%] line-clamp-2">{form.description}</span>
                    </div>
                )}
                <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400 font-medium">Statut</span>
                    <Badge active={form.active}/>
                </div>
                <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400 font-medium">Stratégie de prix</span>
                    <StrategyBadge strategy={form.price_strategy}/>
                </div>
                {form.price_strategy === 'fixed' && (
                    <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400 font-medium">Prix</span>
                        <span className="font-bold text-amber-700">{parseFloat(form.price || 0).toFixed(2)} MAD</span>
                    </div>
                )}
                {(form.available_from || form.available_to) && (
                    <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400 font-medium">Disponibilité</span>
                        <span className="text-gray-700 dark:text-gray-300 flex items-center gap-1">
                            <Clock className="w-3 h-3"/>
                            {form.available_from && form.available_to
                                ? `${form.available_from} – ${form.available_to}`
                                : form.available_from ? `Dès ${form.available_from}` : `Jusqu'à ${form.available_to}`}
                        </span>
                    </div>
                )}
            </div>

            {/* groups summary */}
            <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                    {form.groups.length} groupe{form.groups.length !== 1 ? 's' : ''} de choix
                </p>
                {form.groups.length === 0 ? (
                    <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        ⚠ Aucun groupe — le {menuWord} sera vide.
                    </p>
                ) : (
                    <div className="space-y-2">
                        {form.groups.map((g, i) => (
                            <div key={g._key ?? i} className="flex items-center justify-between bg-gray-50 dark:bg-gray-900/40 rounded-lg px-3 py-2 text-sm">
                                <div className="flex items-center gap-2">
                                    <span className="w-5 h-5 rounded-full bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                                    <span className="font-medium text-gray-800 dark:text-gray-100">{g.name || `Groupe ${i + 1}`}</span>
                                    {g.required && <span className="text-xs text-red-400">*</span>}
                                </div>
                                <span className="text-xs text-gray-400 dark:text-gray-500">{g.items.length} article{g.items.length !== 1 ? 's' : ''} · {g.min}–{g.max} choix</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    const panels = [Step1, Step2, Step3, Step4];

    return (
        <>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col">

                {/* header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">{isEdit ? `Modifier le ${menuWord}` : `Nouveau ${menuWord}`}</h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Étape {step + 1} sur {STEPS.length} — {STEPS[step].label}</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl"><X className="w-5 h-5 text-gray-500 dark:text-gray-400"/></button>
                </div>

                {/* step indicator */}
                <StepIndicator current={step}/>

                {/* body */}
                <div className="flex-1 overflow-y-auto p-6">
                    {panels[step]}
                </div>

                {/* footer nav */}
                <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex gap-3 flex-shrink-0 bg-gray-50 dark:bg-gray-900/40">
                    {step === 0 ? (
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                        >
                            Annuler
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={back}
                            className="flex-1 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                        >
                            ← Retour
                        </button>
                    )}

                    {step < STEPS.length - 1 ? (
                        <button
                            type="button"
                            onClick={next}
                            className="flex-1 py-2.5 bg-[#4DB6AC] hover:bg-teal-600 text-white rounded-xl text-sm font-medium transition"
                        >
                            Suivant →
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={saving}
                            className="flex-1 py-2.5 bg-[#4DB6AC] hover:bg-teal-600 text-white rounded-xl text-sm font-medium transition flex items-center justify-center gap-2 disabled:opacity-60"
                        >
                            {saving && <Loader className="w-4 h-4 animate-spin"/>}
                            {isEdit ? 'Enregistrer' : `Créer le ${menuWord}`}
                        </button>
                    )}
                </div>
            </div>
        </div>

        {showPalette && (
            <div className="fixed inset-0 z-[60] overflow-y-auto">
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={togglePalette}/>
                <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
                    <div
                        className="relative w-full sm:max-w-2xl bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col h-[85vh] sm:h-auto sm:max-h-[85vh]"
                        onClick={e => e.stopPropagation()}
                    >
                        <ColorPalette
                            initialColor={currentColor}
                            onClose={togglePalette}
                            onColorChange={handleColorChange}
                            onImageChange={handleImageChange}
                        />
                    </div>
                </div>
            </div>
        )}
        </>
    );
};

// ─── DeleteConfirmModal ───────────────────────────────────────────────────────

const DeleteConfirmModal = ({menu, onConfirm, onCancel, loading, menuWord = 'menu'}) => (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-500"/>
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white text-center mb-2">Supprimer le {menuWord}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
                Voulez-vous vraiment supprimer <span className="font-semibold text-gray-700 dark:text-gray-300">"{menu.name}"</span> ? Cette action est irréversible.
            </p>
            <div className="flex gap-3">
                <button onClick={onCancel} className="flex-1 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                    Annuler
                </button>
                <button onClick={onConfirm} disabled={loading}
                    className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-medium transition flex items-center justify-center gap-2 disabled:opacity-60">
                    {loading && <Loader className="w-4 h-4 animate-spin"/>}
                    Supprimer
                </button>
            </div>
        </div>
    </div>
);

// ─── MenuPage ─────────────────────────────────────────────────────────────────

const MenuPage = ({createRequestKey = 0, isRestaurant = true}) => {
    // "Menus" (Restaurant) / "Packs" (other commerce types) — same feature, different wording.
    const menuWord = isRestaurant ? 'menu' : 'pack';
    const MenuWord = menuWord.charAt(0).toUpperCase() + menuWord.slice(1);
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [filterActive, setFilterActive] = useState('all');
    const [formModal, setFormModal] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [detailsMenu, setDetailsMenu] = useState(null);

    // Selection state
    const [selectedAll, setSelectedAll] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);

    // Bulk delete state
    const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
    const [bulkDeleting, setBulkDeleting] = useState(false);

    // Sorting state
    const [sortField, setSortField] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');

    const {data, isLoading, error} = useQuery({
        queryKey: ['menus'],
        queryFn: async () => {
            const headers = await getHeaders();
            const res = await api.get('/menus?active=true', {headers});
            return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
        },
    });

    const menus = useMemo(() => (data ?? []).filter(m => {
        const matchSearch = m.name.toLowerCase().includes(search.toLowerCase());
        const matchActive = filterActive === 'all' || (filterActive === 'active' ? m.active : !m.active);
        return matchSearch && matchActive;
    }), [data, search, filterActive]);

    // Sorting
    const sortedMenus = useMemo(() => {
        return [...menus].sort((a, b) => {
            let aVal, bVal;
            switch (sortField) {
                case 'name':
                    aVal = a.name?.toLowerCase() ?? '';
                    bVal = b.name?.toLowerCase() ?? '';
                    break;
                case 'price':
                    aVal = a.price ?? 0;
                    bVal = b.price ?? 0;
                    break;
                case 'groups':
                    aVal = a.groups?.length ?? 0;
                    bVal = b.groups?.length ?? 0;
                    break;
                case 'items':
                    aVal = a.groups?.reduce((s, g) => s + (g.items?.length ?? 0), 0) ?? 0;
                    bVal = b.groups?.reduce((s, g) => s + (g.items?.length ?? 0), 0) ?? 0;
                    break;
                case 'active':
                    aVal = a.active ? 1 : 0;
                    bVal = b.active ? 1 : 0;
                    break;
                default:
                    aVal = a.name?.toLowerCase() ?? '';
                    bVal = b.name?.toLowerCase() ?? '';
            }
            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }, [menus, sortField, sortDirection]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Selection handlers
    const handleSelectAll = useCallback(() => {
        if (selectedAll) {
            setSelectedItems([]);
            setSelectedAll(false);
        } else {
            setSelectedItems(menus.map(m => m.id));
            setSelectedAll(true);
        }
    }, [selectedAll, menus]);

    const handleSelectItem = useCallback((id, e) => {
        e.stopPropagation();
        setSelectedItems(prev => {
            const isSelected = prev.includes(id);
            if (isSelected) {
                return prev.filter(i => i !== id);
            } else {
                return [...prev, id];
            }
        });
    }, []);

    // Effect to sync selectedAll with selectedItems - only runs when selectedItems changes
    useEffect(() => {
        if (menus.length > 0) {
            setSelectedAll(selectedItems.length === menus.length);
        }
    }, [selectedItems.length, menus.length]);

    const invalidate = useCallback(() => {
        queryClient.invalidateQueries(['menus']);
        setSelectedItems([]);
        setSelectedAll(false);
    }, [queryClient]);

    // Plain async handlers (following ProductList pattern)
    const handleToggleMenu = async (menu) => {
        try {
            const headers = await getHeaders();
            await api.patch(`/menus/${menu.id}/toggle`, {}, {headers});
            invalidate();
            toast.success('Statut mis à jour');
        } catch {
            toast.error('Erreur lors du changement de statut');
        }
    };

    const handleDuplicateMenu = async (menu) => {
        try {
            const headers = await getHeaders();
            await api.post(`/menus/${menu.id}/duplicate`, {name: `${menu.name} (copie)`}, {headers});
            invalidate();
            toast.success(`${MenuWord} dupliqué`);
        } catch {
            toast.error('Erreur lors de la duplication');
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            const headers = await getHeaders();
            await api.delete(`/menus/${deleteTarget.id}`, {headers});
            invalidate();
            toast.success(`${MenuWord} supprimé`);
            setDeleteTarget(null);
        } catch {
            toast.error('Erreur lors de la suppression');
        } finally {
            setDeleting(false);
        }
    };

    // Bulk delete handler
    const handleBulkDelete = async () => {
        if (selectedItems.length === 0) return;
        setBulkDeleting(true);
        try {
            const headers = await getHeaders();
            await Promise.all(selectedItems.map(id => api.delete(`/menus/${id}`, {headers})));
            invalidate();
            toast.success(`${selectedItems.length} ${menuWord}${selectedItems.length > 1 ? 's' : ''} supprimé${selectedItems.length > 1 ? 's' : ''}`);
            setShowBulkDeleteConfirm(false);
        } catch {
            toast.error('Erreur lors de la suppression');
        } finally {
            setBulkDeleting(false);
        }
    };

    const handleViewDetails = useCallback((menu) => setDetailsMenu(menu), []);
    const handleEdit = useCallback((menu) => setFormModal(menu), []);
    const handleDeleteClick = useCallback((menu) => setDeleteTarget(menu), []);

    // Helper to count items in a menu
    const getItemCount = useCallback((menu) => menu.groups?.reduce((s, g) => s + (g.items?.length ?? 0), 0) ?? 0, []);

    useEffect(() => {
        if (createRequestKey > 0) {
            setFormModal({});
        }
    }, [createRequestKey]);

    return (
        <div>
            <div>
                    {/* toolbar */}
                    <div className="flex flex-col sm:flex-row">
                        <div className="relative flex-1">
                            <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                            <input
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder={`Rechercher un ${menuWord}…`}
                                className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all placeholder:text-gray-400 shadow-sm"
                            />
                        </div>
                        <AppleSelect
                            value={filterActive}
                            onChange={e => setFilterActive(e.target.value)}
                            className="border border-gray-200 dark:border-gray-700 px-3 py-3 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary transition-all shadow-sm"
                        >
                            <option value="all">Tous</option>
                            <option value="active">Actifs</option>
                            <option value="inactive">Inactifs</option>
                        </AppleSelect>
                    </div>

                    {/* stats bar */}
                    {!isLoading && (data ?? []).length > 0 && (
                        <div className="flex gap-4 px-4 py-2.5 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                            <span className="flex items-center gap-1"><BarChart2 className="w-3.5 h-3.5"/>{(data ?? []).length} {menuWord}{(data ?? []).length > 1 ? 's' : ''} au total</span>
                            <span className="flex items-center gap-1 text-green-600 dark:text-green-400"><CheckCircle className="w-3.5 h-3.5"/>{(data ?? []).filter(m => m.active).length} actifs</span>
                        </div>
                    )}

                    {/* Selection bar */}
                    {selectedItems.length > 0 && (
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 bg-primary/10 dark:bg-primary/20 border-l-4 border-primary shadow-sm">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-primary flex items-center justify-center rounded-full shadow">
                                    <CheckCircle className="w-5 h-5 text-white"/>
                                </div>
                                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                    {selectedItems.length} {menuWord}{selectedItems.length > 1 ? 's' : ''} sélectionné{selectedItems.length > 1 ? 's' : ''}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => { setSelectedItems([]); setSelectedAll(false); }}
                                    className="flex items-center px-3 py-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm"
                                >
                                    Tout désélectionner
                                </button>
                                <button
                                    onClick={() => setShowBulkDeleteConfirm(true)}
                                    className="flex items-center gap-2 px-3 py-1.5 text-red-500 hover:text-white hover:bg-red-500 rounded-lg transition-colors text-sm"
                                >
                                    <Trash2 className="w-4 h-4"/> Supprimer la sélection
                                </button>
                            </div>
                        </div>
                    )}

                    {/* content */}
                    {isLoading ? (
                        <div className="flex flex-col justify-center items-center h-64 space-y-4">
                            <Loader className="w-10 h-10 text-primary animate-spin"/>
                            <p className="text-gray-500 dark:text-gray-400">Chargement des {menuWord}s...</p>
                        </div>
                    ) : error ? (
                        <div className="flex flex-col items-center py-16 text-red-500 gap-2">
                            <AlertCircle className="w-8 h-8"/>
                            <p className="text-sm">Erreur lors du chargement des {menuWord}s</p>
                        </div>
                    ) : menus.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 px-4">
                            <BookOpen className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4"/>
                            <p className="font-medium text-gray-500 dark:text-gray-400 mb-1">Aucun {menuWord} trouvé</p>
                            <p className="text-sm text-gray-400 dark:text-gray-500">Créez votre premier {menuWord} en cliquant sur "Nouveau {menuWord}"</p>
                        </div>
                    ) : (
                        <div className="bg-white dark:bg-gray-800 shadow-sm overflow-hidden border border-gray-200 dark:border-gray-700">
                            {/* Desktop Table */}
                            <div className="hidden lg:block overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                                        <tr>
                                            <th className="px-6 py-4 text-left">
                                                <input
                                                    type="checkbox"
                                                    className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                                    checked={selectedAll}
                                                    onChange={handleSelectAll}
                                                />
                                            </th>
                                            <SortableHeader field="name" currentField={sortField} direction={sortDirection} onSort={handleSort}>
                                                Nom
                                            </SortableHeader>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                                                Stratégie
                                            </th>
                                            <SortableHeader field="price" currentField={sortField} direction={sortDirection} onSort={handleSort}>
                                                Prix
                                            </SortableHeader>
                                            <SortableHeader field="groups" currentField={sortField} direction={sortDirection} onSort={handleSort}>
                                                Groupes
                                            </SortableHeader>
                                            <SortableHeader field="items" currentField={sortField} direction={sortDirection} onSort={handleSort}>
                                                Articles
                                            </SortableHeader>
                                            <SortableHeader field="active" currentField={sortField} direction={sortDirection} onSort={handleSort}>
                                                Statut
                                            </SortableHeader>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                                                Disponibilité
                                            </th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                        {sortedMenus.map(menu => (
                                            <tr
                                                key={menu.id}
                                                className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                                                onClick={() => handleViewDetails(menu)}
                                            >
                                                <td className="px-6 py-4">
                                                    <input
                                                        type="checkbox"
                                                        className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                                        checked={selectedItems.includes(menu.id)}
                                                        onChange={(e) => handleSelectItem(menu.id, e)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <PackThumb name={menu.name} color={menu.color} imageUrl={menu.image_url} className="h-12 w-12 rounded-lg"/>
                                                        <div>
                                                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{menu.name}</div>
                                                            {menu.description && (
                                                                <div className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-[200px]">{menu.description}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <StrategyBadge strategy={menu.price_strategy}/>
                                                </td>
                                                <td className="px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                    {menu.price_strategy === 'fixed' && menu.price != null ? (
                                                        <span>{Number(menu.price).toFixed(2)} MAD</span>
                                                    ) : (
                                                        <span className="text-gray-400">—</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                                                        {menu.groups?.length ?? 0}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                                                        {getItemCount(menu)}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <Badge active={menu.active}/>
                                                </td>
                                                <td className="px-6 py-4">
                                                    {(menu.available_from || menu.available_to) ? (
                                                        <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                                                            <Clock className="w-3 h-3"/>
                                                            <span>
                                                                {menu.available_from && menu.available_to
                                                                    ? `${menu.available_from} – ${menu.available_to}`
                                                                    : menu.available_from ? `Dès ${menu.available_from}` : `Jusqu'à ${menu.available_to}`
                                                                }
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400 text-sm">—</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <MenuActions
                                                        menu={menu}
                                                        onEdit={handleEdit}
                                                        onToggle={handleToggleMenu}
                                                        onDuplicate={handleDuplicateMenu}
                                                        onDelete={handleDeleteClick}
                                                        onViewDetails={handleViewDetails}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Cards */}
                            <div className="lg:hidden divide-y divide-gray-200 dark:divide-gray-700">
                                {sortedMenus.map(menu => (
                                    <div
                                        key={menu.id}
                                        className="p-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                                        onClick={() => handleViewDetails(menu)}
                                    >
                                        <div className="flex items-start gap-3 mb-3">
                                            <input
                                                type="checkbox"
                                                className="mt-1 w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                                checked={selectedItems.includes(menu.id)}
                                                onChange={(e) => handleSelectItem(menu.id, e)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                            <PackThumb name={menu.name} color={menu.color} imageUrl={menu.image_url} className="h-10 w-10 rounded-lg"/>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate mb-1">{menu.name}</h3>
                                                {menu.description && (
                                                    <p className="text-xs text-gray-400 dark:text-gray-500 truncate mb-2">{menu.description}</p>
                                                )}
                                                <div className="flex flex-wrap gap-1.5">
                                                    <Badge active={menu.active}/>
                                                    <StrategyBadge strategy={menu.price_strategy}/>
                                                </div>
                                            </div>
                                            <MenuActions
                                                menu={menu}
                                                onEdit={handleEdit}
                                                onToggle={handleToggleMenu}
                                                onDuplicate={handleDuplicateMenu}
                                                onDelete={handleDeleteClick}
                                                onViewDetails={handleViewDetails}
                                            />
                                        </div>
                                        <div className="grid grid-cols-3 gap-3 text-sm">
                                            <div>
                                                <span className="text-gray-500 dark:text-gray-400 text-xs block mb-1">Prix</span>
                                                <div className="font-semibold text-gray-900 dark:text-gray-100">
                                                    {menu.price_strategy === 'fixed' && menu.price != null
                                                        ? `${Number(menu.price).toFixed(2)} MAD`
                                                        : <span className="text-gray-400">—</span>
                                                    }
                                                </div>
                                            </div>
                                            <div>
                                                <span className="text-gray-500 dark:text-gray-400 text-xs block mb-1">Groupes</span>
                                                <div className="font-semibold text-gray-900 dark:text-gray-100">{menu.groups?.length ?? 0}</div>
                                            </div>
                                            <div>
                                                <span className="text-gray-500 dark:text-gray-400 text-xs block mb-1">Articles</span>
                                                <div className="font-semibold text-gray-900 dark:text-gray-100">{getItemCount(menu)}</div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
            </div>

            {/* modals */}
            {formModal !== null && (
                <MenuFormModal
                    initial={formModal?.id ? formModal : null}
                    onClose={() => setFormModal(null)}
                    onSaved={invalidate}
                    menuWord={menuWord}
                />
            )}

            {deleteTarget && (
                <DeleteConfirmModal
                    menu={deleteTarget}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteTarget(null)}
                    loading={deleting}
                    menuWord={menuWord}
                />
            )}

            {detailsMenu && (
                <MenuDetailsModal
                    menu={detailsMenu}
                    onClose={() => setDetailsMenu(null)}
                    onEdit={m => setFormModal(m)}
                />
            )}

            {showBulkDeleteConfirm && (
                <BulkDeleteConfirmModal
                    count={selectedItems.length}
                    onConfirm={handleBulkDelete}
                    onCancel={() => setShowBulkDeleteConfirm(false)}
                    loading={bulkDeleting}
                    menuWord={menuWord}
                />
            )}
        </div>
    );
};

export default MenuPage;
